import { Phone, MessageCircle, ShieldCheck, Zap, MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUSINESS, waLink } from "@/lib/businessInfo";

const HERO_BG =
    "https://images.pexels.com/photos/17996048/pexels-photo-17996048.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800";

const Hero = () => {
    const { lang, t } = useLanguage();
    const gu = lang === "gu" ? "font-gujarati" : "";

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative min-h-[92vh] flex items-center pt-[104px] overflow-hidden bg-white"
        >
            {/* Background image */}
            <div className="absolute inset-0 -z-10">
                <img
                    src={HERO_BG}
                    alt="Ahmedabad financial district"
                    className="w-full h-full object-cover object-center"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/92 to-white/30" />
                <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-white/40" />
            </div>

            {/* Decorative blue accent */}
            <div className="absolute top-[20%] -left-24 w-[420px] h-[420px] rounded-full bg-[#0B308A]/5 blur-3xl -z-10" />
            <div className="absolute bottom-[5%] right-[10%] w-[280px] h-[280px] rounded-full bg-[#FACC15]/10 blur-3xl -z-10" />

            <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center w-full">
                <div className="lg:col-span-7 sg-fade-up">
                    <span
                        data-testid="hero-overline"
                        className={`inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-[#0B308A] bg-[#EFF6FF] border border-[#0B308A]/10 px-4 py-2 rounded-full ${gu}`}
                    >
                        <span className="w-1.5 h-1.5 bg-[#0B308A] rounded-full" />
                        {t("hero.overline")}
                    </span>

                    <h1
                        data-testid="hero-title"
                        className={`mt-6 text-4xl sm:text-5xl lg:text-[64px] font-display font-bold leading-[1.05] tracking-tight text-slate-900 ${gu}`}
                    >
                        {t("hero.title_a")}{" "}
                        <span className="text-[#0B308A] relative inline-block">
                            {t("hero.title_b")}
                            <svg
                                className="absolute -bottom-2 left-0 w-full"
                                viewBox="0 0 300 12"
                                preserveAspectRatio="none"
                            >
                                <path
                                    d="M2 9 Q 150 -2 298 7"
                                    stroke="#FACC15"
                                    strokeWidth="3"
                                    fill="none"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </span>
                    </h1>

                    <p
                        data-testid="hero-subtitle"
                        className={`mt-7 text-lg text-slate-600 leading-relaxed max-w-xl ${gu}`}
                    >
                        {t("hero.subtitle")}
                    </p>

                    <div className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
                        <a
                            data-testid="hero-call-btn"
                            href={`tel:${BUSINESS.phoneTel[0]}`}
                            className={`group inline-flex items-center gap-2.5 bg-[#0B308A] text-white px-7 py-4 rounded-sm font-semibold text-[15px] shadow-[0_12px_30px_rgba(11,48,138,0.28)] hover:bg-[#1E3A8A] hover:translate-y-[-2px] transition-all ${gu}`}
                        >
                            <Phone className="w-[18px] h-[18px]" strokeWidth={2.4} />
                            {t("hero.cta_call")}
                        </a>
                        <a
                            data-testid="hero-wa-btn"
                            href={waLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`group inline-flex items-center gap-2.5 bg-white text-[#0B308A] border-2 border-[#0B308A] px-7 py-[14px] rounded-sm font-semibold text-[15px] hover:bg-[#0B308A] hover:text-white transition-all ${gu}`}
                        >
                            <MessageCircle className="w-[18px] h-[18px]" strokeWidth={2.4} />
                            {t("hero.cta_whatsapp")}
                        </a>
                    </div>

                    {/* Trust badges */}
                    <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-[13px] text-slate-700">
                        <span className={`inline-flex items-center gap-2 ${gu}`}>
                            <ShieldCheck className="w-4 h-4 text-[#0B308A]" />
                            {t("hero.badge_1")}
                        </span>
                        <span className={`inline-flex items-center gap-2 ${gu}`}>
                            <MapPin className="w-4 h-4 text-[#0B308A]" />
                            {t("hero.badge_2")}
                        </span>
                        <span className={`inline-flex items-center gap-2 ${gu}`}>
                            <Zap className="w-4 h-4 text-[#0B308A]" />
                            {t("hero.badge_3")}
                        </span>
                    </div>
                </div>

                {/* Right floating info card */}
                <div className="lg:col-span-5 hidden lg:block">
                    <div className="relative">
                        <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#FACC15]/30 rounded-sm -z-10" />
                        <div className="absolute -bottom-6 -right-6 w-40 h-40 border-2 border-[#0B308A]/20 rounded-sm -z-10" />
                        <div className="bg-white border border-slate-100 shadow-[0_30px_60px_rgba(11,48,138,0.12)] p-8 rounded-sm">
                            <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0B308A] ${gu}`}>
                                {t("hero.quick_stats")}
                            </div>
                            <div className="mt-6 grid grid-cols-2 gap-6">
                                <div>
                                    <div className="font-display text-4xl font-bold text-slate-900">
                                        7+
                                    </div>
                                    <div className={`text-sm text-slate-500 mt-1 ${gu}`}>
                                        {t("hero.stat_years")}
                                    </div>
                                </div>
                                <div>
                                    <div className="font-display text-4xl font-bold text-slate-900">
                                        5k+
                                    </div>
                                    <div className={`text-sm text-slate-500 mt-1 ${gu}`}>
                                        {t("hero.stat_loans")}
                                    </div>
                                </div>
                                <div>
                                    <div className="font-display text-4xl font-bold text-slate-900">
                                        25+
                                    </div>
                                    <div className={`text-sm text-slate-500 mt-1 ${gu}`}>
                                        {t("hero.stat_districts")}
                                    </div>
                                </div>
                                <div>
                                    <div className="font-display text-4xl font-bold text-slate-900">
                                        98%
                                    </div>
                                    <div className={`text-sm text-slate-500 mt-1 ${gu}`}>
                                        {t("hero.stat_satisfaction")}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-8 pt-6 border-t border-slate-100 text-sm">
                                <div className={`text-slate-500 ${gu}`}>{t("hero.talk_to_us")}</div>
                                <a
                                    href={`tel:${BUSINESS.phoneTel[0]}`}
                                    className="block mt-1 font-display font-bold text-xl text-[#0B308A] hover:underline"
                                >
                                    {BUSINESS.phones[0]}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
